// rounds/public-catalog types — Zod schemas + inferred types
import { z } from "zod";
import { RoundStatusSchema } from "@modules/rounds/_shared";

/**
 * Datetime fields in the public round payload.
 *
 * `{ offset: true }` is REQUIRED here, not tolerance — this endpoint is not UTC-normalized the way
 * the staff ones are, and a single response mixes both forms (verified against the running stack):
 *
 * ```
 * "server_now":  "2026-07-31T01:40:35.896301+01:00"   ← DRF, rendered in TIME_ZONE
 * "opening_at":  "2026-07-30T17:44:19.383548Z"        ← utc_iso(), frozen in the snapshot
 * "closing_at":  "2026-08-06T18:12:00+01:00"          ← DRF, rendered in TIME_ZONE
 * ```
 *
 * The staff round payload goes through `utc_iso()` for every field, so it is uniformly `…Z`; the
 * public serializer only frozen-copies `opening_at` and leaves DRF to render the rest. Both are
 * unambiguous instants, so nothing here is broken — but it IS an inconsistency between two
 * endpoints over the same entity, and worth raising rather than quietly absorbing.
 *
 * Every consumer must therefore accept both. A schema pinned to `Z` would make `parseSafe` drop
 * every round and blank a page the public sees.
 */
const PublicDateTimeSchema = z.iso.datetime({ offset: true });

/**
 * One entry of the public round catalogue (`GET /acquire/public/intake-rounds/`), matching
 * `serialize_public_round()` in `modules/acquire/public_services.py`.
 *
 * A DELIBERATELY narrower round than the staff list's: no `capacity_target`, no `config_revision`,
 * no `configuration_editable` — a public visitor has no business knowing the seat target or the
 * optimistic-concurrency token. What it gains are three derived booleans the backend computes so
 * that no client has to:
 *
 * - `accepting_applications` — the authority for an apply affordance. A round can be `OPEN` and
 *   still refuse new applications, so status alone is NOT the answer.
 * - `configuration_final` — the configuration is frozen. False on a `SCHEDULED` round, whose name
 *   and dates are live but whose specifics may still change.
 * - `requirements_available` — the detail endpoint will return eligibility + documents rather
 *   than `null`.
 *
 * `status` reuses the module's full enum rather than the four values these endpoints can emit
 * (`SCHEDULED`/`OPEN`/`CLOSING_SOON`/`CLOSED`). The narrower set is the backend's filter to
 * enforce, not ours to restate — and if it ever widens, a round should appear with a quiet badge
 * rather than silently vanish from the catalogue.
 */
export const PublicRoundSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Blank on a round whose author left it empty — `allow_blank`, never null. */
  description: z.string(),
  status: RoundStatusSchema,
  opening_at: PublicDateTimeSchema,
  closing_at: PublicDateTimeSchema,
  /**
   * Whether the round's authors consider these dates settled.
   *
   * Present on the SUMMARY serializer but absent from the integration guide's example payload —
   * found by reading `serialize_public_round()`, which is why the rule says check the running API
   * rather than the doc. A `false` here is the round saying "provisional", and the preview must
   * not print a provisional deadline in the same voice as a fixed one.
   */
  dates_are_definitive: z.boolean(),
  /** IANA zone the round's deadlines are authored in ("Africa/Algiers"). */
  timezone: z.string(),
  accepting_applications: z.boolean(),
  configuration_final: z.boolean(),
  requirements_available: z.boolean(),
});

export type PublicRoundSummary = z.infer<typeof PublicRoundSummarySchema>;

/**
 * The catalogue envelope — NOT the DRF pagination shape the staff lists use, so the shared
 * `PaginatedResponse<T>` does not apply here. This endpoint takes no `?page=` and returns every
 * publicly visible round in one response, already filtered and ordered server-side: there is no
 * `count`/`next`/`previous` to parse and no page state for a hook to own.
 *
 * `server_now` is the point of the envelope: the clock every deadline must be measured against.
 */
export const PublicRoundListResponseSchema = z.object({
  server_now: PublicDateTimeSchema,
  intake_rounds: z.array(z.unknown()),
});

/** The catalogue, parsed — the rounds plus the clock they are read against. */
export interface PublicRoundCatalog {
  /** Server clock at response time. The authority for deadline maths, never `Date.now()`. */
  serverNow: string;
  rounds: PublicRoundSummary[];
}

/**
 * One document an applicant will have to upload, as the PUBLIC endpoint describes it.
 *
 * Localized server-side: unlike the staff shape this carries a single `name`/`description` in the
 * requested locale, not a `_fr`/`_ar`/`_en` triplet. So the query key must include the locale.
 */
export const PublicDocumentRequirementSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  is_required: z.boolean(),
  /** Lower-case, no dot ("pdf", "docx") — for the "accepted formats" hint. */
  accepted_extensions: z.array(z.string()),
  accepted_mime_types: z.array(z.string()),
  max_size_bytes: z.number().int(),
  max_files: z.number().int(),
});

export type PublicDocumentRequirement = z.infer<
  typeof PublicDocumentRequirementSchema
>;

/**
 * The eligibility a round publishes.
 *
 * ⚠️ `failed_gates_behavior` is hard-coded to `"human_review_only"` server-side, and that is the
 * whole contract: **failing a gate never auto-rejects an application.** So this is material for an
 * advisory "check you qualify" panel and must NEVER become a client-side blocker — a hard gate here
 * would invent a rejection the product does not have.
 */
export const PublicEligibilitySchema = z.object({
  age_min: z.number().int(),
  age_max: z.number().int(),
  residency_required: z.boolean(),
  residency_description: z.string(),
  /** `ALL` — any wilaya; `SELECTED` — only `wilaya_codes`. */
  wilaya_mode: z.string(),
  /** Two-digit administrative codes ("16"). Empty when `wilaya_mode` is `ALL`. */
  wilaya_codes: z.array(z.string()),
  /**
   * The minimum education level, as a catalogue CODE and as the localized LABEL for that code.
   *
   * Both added by `fix(acquire): allow drafts before approved configuration` and absent from the
   * schema until this pass. The label is resolved server-side against the round's own frozen
   * catalogue — which is exactly why the client must not resolve it itself: that hand-mirror is
   * what broke `PUT /eligibility/` when the manifest went v1 → v3 (see rules/rounds.md).
   *
   * Both are `allow_blank`, so a round that sets no minimum sends `""` — render nothing, not "".
   */
  education_minimum_code: z.string(),
  education_minimum: z.string(),
  failed_gates_behavior: z.string(),
});

export type PublicEligibility = z.infer<typeof PublicEligibilitySchema>;

/** Eligibility + documents, present only once the round's configuration is frozen. */
export const PublicRequirementsSchema = z.object({
  eligibility: PublicEligibilitySchema,
  documents: z.object({
    /** `CONFIGURED` | `EXPLICITLY_NONE` — the latter ships an empty `requirements`. */
    policy: z.string(),
    requirements: z.array(PublicDocumentRequirementSchema),
  }),
});

/**
 * One step of the timeline a round publishes (`milestones[]` on the public detail).
 *
 * `label` is already localized and already falls back to `kind` server-side, so it is never blank.
 * That fallback is why US-E1-14 insists the labels be configured: a round that left them empty
 * shows an applicant the raw enum.
 *
 * **Two ways to be scheduled, and the client must not convert between them.** `EXACT` carries a
 * real instant; `AFTER_CLOSING` carries `offset_value` + `offset_unit`. The server also computes
 * `effective_at` for the offset case — using `MONTHS = 30 days`, which is not a calendar month.
 * The preview renders the offset in WORDS ("about 6 weeks after closing") rather than a date, so
 * there is no client arithmetic to disagree with the server's. See rules/rounds.md § step 5.
 */
export const PublicMilestoneSchema = z.object({
  id: z.string(),
  /** `EXAM_START` | `RESULTS_ANNOUNCEMENT` | `PROGRAM_START` — passed through, never branched on. */
  kind: z.string(),
  /** `EXACT` | `AFTER_CLOSING`. Strict-ish: this one DOES decide what renders. */
  schedule_type: z.enum(["EXACT", "AFTER_CLOSING"]),
  exact_at: PublicDateTimeSchema.nullable(),
  effective_at: PublicDateTimeSchema.nullable(),
  offset_value: z.number().int().nullable(),
  offset_unit: z.enum(["DAYS", "WEEKS", "MONTHS"]).nullable().catch(null),
  display_order: z.number().int(),
  is_active: z.boolean(),
  /** Localized, never blank — the server falls back to `kind`. */
  label: z.string(),
});

export type PublicMilestone = z.infer<typeof PublicMilestoneSchema>;

/** One question of the round's own form, as the applicant will meet it. Read-only here. */
export const PublicSchemaQuestionSchema = z.object({
  id: z.string(),
  question_text: z.string(),
  help_text: z.string(),
  /**
   * Deliberately NOT an enum. The preview never branches on it — it renders the wording, not a
   * control — so a strict enum could only ever cost the whole list the first time the backend
   * grows an input type. That is precisely how `applicant_step` blanked every competency group.
   */
  input_type: z.string(),
  is_contextual: z.boolean().default(false),
  competencies: z
    .array(
      z.object({
        label: z.string(),
        display_order: z.number().int(),
        is_primary: z.boolean().default(false),
      })
    )
    .default([]),
  is_required: z.boolean(),
});

/** One fixed applicant field (`applicant.sections[].fields[]`), as the wizard will render it. */
export const PublicSchemaFieldSchema = z.object({
  /** The stable field key (`weekly_commitment_code`), not a UUID. */
  id: z.string(),
  section: z.string(),
  label: z.string(),
  help_text: z.string(),
  input_type: z.string(),
  is_required: z.boolean(),
  display_order: z.number().int(),
  /** `SCORED` | `CONTEXTUAL` | `OPERATIONAL`. Passed through — the preview does not grade. */
  data_kind: z.string(),
});

/**
 * One of the four applicant sections, server-ordered and server-titled.
 *
 * ⚠️ **The four titles come from HERE, never from our i18n.** `_localized_schema` emits
 * `founder`/`project`/`team`/`practical` with localized titles, and the whole point of the Aperçu
 * (US-E11-01) is that it is a READ of the round's frozen configuration: if this page and the wizard
 * ever disagree, that must be a data bug, not a copy bug. Translating the tab names locally would
 * manufacture exactly the divergence the story forbids.
 */
export const PublicSchemaSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  fields: z.array(PublicSchemaFieldSchema),
});

/**
 * The applicant-facing rendering contract, embedded in the public round detail.
 *
 * ⚠️ Served through DRF's `DictField` — i.e. **completely unvalidated server-side**, a passthrough
 * of whatever `_localized_schema()` builds. So the shape here was read off that function, not off a
 * serializer, and it is parsed with `parseSafe` rather than `parseWith`: a change to it should cost
 * the questions section, not the whole page.
 *
 * Two lists, and they are not the same thing:
 * - `applicant.sections[].fields` — the FIXED dossier, identical in every round.
 * - `form.sections[].questions` — the questions THIS round's administrator authored, placed by
 *   `applicant_step`.
 *
 * The preview shows both, because the applicant meets both.
 */
export const PublicApplicantSchemaSchema = z.object({
  applicant: z.object({ sections: z.array(PublicSchemaSectionSchema) }),
  form: z.object({
    sections: z.array(
      z.object({
        id: z.string(),
        title: z.string(),
        /**
         * Which of the four tabs this section's questions land on. Lower-cased on read because
         * the two halves of the payload disagree in CASE: `applicant.sections[].key` is
         * `"founder"` while `form.sections[].applicant_step` is `"FOUNDER"`. Verified against
         * `_localized_schema` — the applicant titles map is keyed lower-case, the section field
         * defaults to `"FOUNDER"`.
         */
        applicant_step: z
          .string()
          .default("FOUNDER")
          .transform((value) => value.toLowerCase()),
        questions: z.array(PublicSchemaQuestionSchema),
      })
    ),
  }),
});

export type PublicApplicantSchema = z.infer<typeof PublicApplicantSchemaSchema>;

/**
 * One publicly visible round in full (`GET /acquire/public/intake-rounds/{id}/?locale=`).
 *
 * The summary plus what an applicant needs before committing: the requirements they must satisfy
 * and the documents they should gather. Verified against the running stack.
 *
 * `requirements` is **null whenever the configuration is not frozen** — a `SCHEDULED` round has
 * real dates but no published specifics. Rendering it as an empty list instead of "not published
 * yet" would tell an applicant there is nothing to prepare, which is the opposite of true.
 *
 * `direction` is the server's opinion of the locale's text direction. Deliberately parsed and
 * ignored: `<html dir>` is already driven by i18next, and honouring a second source of direction
 * would let the two disagree.
 */
export const PublicRoundDetailSchema = PublicRoundSummarySchema.extend({
  server_now: PublicDateTimeSchema,
  locale: z.string(),
  direction: z.string(),
  requirements: PublicRequirementsSchema.nullable(),
  /**
   * Empty on a Scheduled round, and empty on a round whose author configured no timeline — which
   * are different facts with the same shape. Neither is an error; the section simply does not
   * render. US-E11-04 wants the timeline stated, but stating one the round has not configured
   * would be the screen inventing a promise.
   */
  milestones: z.array(PublicMilestoneSchema),
  /**
   * Parsed OUTSIDE this schema, so it stays `unknown` here.
   *
   * The field is `required=False` on the serializer AND null for a Scheduled round, and its
   * contents are an unvalidated `DictField`. Making it a required strict object would let one
   * upstream shape change 404 an otherwise fine public page. See `getPublicRound`.
   */
  applicant_schema: z.unknown().nullable().optional(),
});

export type PublicRoundDetail = z.infer<typeof PublicRoundDetailSchema>;

/** The round detail plus its separately-parsed applicant schema. What the preview screen renders. */
export interface PublicRoundPreview {
  round: PublicRoundDetail;
  /**
   * `null` when the round has not frozen its configuration (Scheduled), or when the payload no
   * longer matches. Both mean the same thing to the screen: show the round, not the questions.
   */
  schema: PublicApplicantSchema | null;
}

/**
 * Whole days from the SERVER's clock to `iso`, rounded up — `1` on the last day, `0` once the
 * deadline has passed.
 *
 * `serverNow` is a required argument rather than an internal `Date.now()` on purpose. A visitor's
 * device clock can be days wrong, and the failure it produces is the bad kind: a public page
 * telling someone they still have a week to apply to a round that closed yesterday. Passing the
 * server's clock makes the countdown as correct as the response it came with.
 *
 * `null` when either instant is unparseable, so the caller renders nothing rather than "NaN days".
 */
export function daysUntil(iso: string, serverNow: string): number | null {
  const target = new Date(iso).getTime();
  const from = new Date(serverNow).getTime();
  if (Number.isNaN(target) || Number.isNaN(from)) return null;
  return Math.max(0, Math.ceil((target - from) / 86_400_000));
}

/** One line of the dossier preview: a question the applicant will be asked, and nothing else. */
export interface PreviewQuestion {
  key: string;
  label: string;
  isContextual: boolean;
  isRequired: boolean;
}

/** One of the four tabs, as the preview lists it. */
export interface PreviewGroup {
  key: string;
  /** The server's title for the section. Never ours — see `PublicSchemaSectionSchema`. */
  title: string;
  /**
   * The round-authored section titles that land on this tab, in order — the group's one-line
   * summary ("Validation client · Viabilité · Stade").
   *
   * The design draws this line for every group, but only the ROUND's sections carry a title in the
   * payload; the fixed fields carry none. So a group with no authored questions summarises to an
   * empty list and the line is omitted. **Do not fill the gap with a hardcoded map of field key →
   * heading.** That is the `EDUCATION_LEVEL_CODES` mistake: a hand-mirror of server data that reads
   * correctly until the server's list moves, then lies silently.
   */
  subtitles: string[];
  questions: PreviewQuestion[];
}

/**
 * Flatten the applicant schema into the four groups the Aperçu lists, in tab order.
 *
 * Both halves of the payload feed one list, because an applicant meets one list: the fixed dossier
 * fields (`applicant.sections[].fields`, already sorted server-side by `display_order`) followed by
 * the questions this round's administrator authored (`form.sections[].questions`, placed by
 * `applicant_step`).
 *
 * Sections are driven by `applicant.sections` rather than by iterating the form: the four tabs
 * exist whether or not a round authored anything for them, and a round with no `team` questions
 * still asks the fixed team questions.
 */
export function toPreviewGroups(schema: PublicApplicantSchema): PreviewGroup[] {
  /**
   * Only sections that actually carry questions.
   *
   * Verified against the running API: a real round returns SEVEN form sections, four of which hold
   * zero questions and duplicate the tab names ("Porteur", "Projet", "Équipe", "Détails
   * pratiques"). Summarising those would print the group's own title back at it as its subtitle.
   */
  const authored = schema.form.sections.filter(
    (item) => item.questions.length > 0
  );

  const groups = schema.applicant.sections.map((section) => {
    const mine = authored.filter((item) => item.applicant_step === section.key);

    return {
      key: section.key,
      title: section.title,
      subtitles: mine.map((item) => item.title).filter(Boolean),
      questions: [
        ...section.fields.map((field) => ({
          key: field.id,
          label: field.label,
          isRequired: field.is_required,
          isContextual: false,
        })),
        ...mine.flatMap((item) =>
          item.questions.map((question) => ({
            key: question.id,
            label: question.question_text,
            isContextual: question.is_contextual,
            isRequired: question.is_required,
          }))
        ),
      ],
    };
  });

  /**
   * Anything the four tabs did not claim, appended as its own group.
   *
   * ⚠️ Not defensive coding — a live round already returns a `CONTEXT` section, which matches none
   * of `founder`/`project`/`team`/`practical`. It holds no questions today, so dropping it is
   * currently invisible; the moment an administrator writes one there it would vanish from a page
   * whose entire promise is that it shows the WHOLE dossier.
   *
   * `DefaultRoundFormSection` is a table the backend owns and has already grown from one row to
   * seven. A silent `filter` against a fixed list of four is the same bet that blanked every
   * competency group when `applicant_step` gained `TEAM` (rules/rounds.md) — so unmatched sections
   * surface under the server's own title instead of disappearing.
   */
  const claimed = new Set(schema.applicant.sections.map((item) => item.key));
  const orphans = authored
    .filter((item) => !claimed.has(item.applicant_step))
    .map((item) => ({
      key: item.id,
      title: item.title,
      subtitles: [],
      questions: item.questions.map((question) => ({
        key: question.id,
        label: question.question_text,
        isContextual: question.is_contextual,
        isRequired: question.is_required,
      })),
    }));

  return [...groups, ...orphans];
}

/** Every question across every group — the "45 questions" the header counts. */
export function countPreviewQuestions(groups: PreviewGroup[]): number {
  return groups.reduce((total, group) => total + group.questions.length, 0);
}
